import { getValidAccessToken } from "./auth-session";
import { getApiOrigin } from "./api-base";

export type AuthFetchOptions = RequestInit & {
  /** When set, skips the stored-session lookup and uses this token verbatim (e.g. right after login, before it's persisted). */
  accessToken?: string | null;
};

/**
 * Browser fetch that attaches a valid access token when present, transparently
 * refreshing it first if it's expired or about to expire (see auth-session.ts).
 * Uses `credentials: "include"` for same-origin / proxied API; omits for explicit cross-origin `VITE_API_BASE_URL` (Bearer only).
 * Always uses `cache: "no-store"` so conditional GET (304) cannot strip JSON bodies.
 */
export async function authFetch(input: RequestInfo | URL, init?: AuthFetchOptions): Promise<Response> {
  const { accessToken: tokenHint, ...rest } = init ?? {};

  const token = tokenHint !== undefined ? tokenHint : await getValidAccessToken();

  const headers = new Headers(rest.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const crossOrigin = Boolean(getApiOrigin());
  return fetch(input, {
    ...rest,
    headers,
    cache: rest.cache ?? "no-store",
    credentials: rest.credentials ?? (crossOrigin ? "omit" : "include"),
  });
}
